import { useEffect, useState } from 'react';
import { usePatient } from '@hooks/usePatient';
import { useMedplum } from '@medplum/react';
import { Coding, Condition } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
import { ARKON, HL7, PREFIXES, SYSTEMS } from '@/consts/systems.consts';
import { notifications } from '@mantine/notifications';
import {
  FIND_ICD10_FROM_ORPHA,
  FIND_ORPHA_FROM_OMIM,
  HPO_ANNOTATION_API_URL,
  SEARCH_OMIM_DISEASE,
} from '@/consts/api.consts';
import { HpoItem } from '@/types/hpo.types';

export interface OmimDisease {
  id: string;
  name: string;
  description: string;
  conditionId?: string;
  verificationStatus?: string;
}

interface OmimSearchResult {
  id: string;
  name: string;
  description: string;
  mondoId?: string;
}

// -----------------------------------------------------------------------------
export const useOmimDisease = (initialCondition?: Condition) => {
  const [omimData, setOmimData] = useState<OmimDisease[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<OmimSearchResult[]>([]);
  const [selectedOmim, setSelectedOmim] = useState<
    OmimSearchResult | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [orphaCode, setOrphaCode] = useState<string | undefined>();
  const [isFetchingOrpha, setIsFetchingOrpha] = useState(false);
  const [icdCode, setIcdCode] = useState<string | undefined>();
  const [isFetchingIcd, setIsFetchingIcd] = useState(false);
  const [hpoTerms, setHpoTerms] = useState<HpoItem[]>([]);

  const patient = usePatient();
  const medplum = useMedplum();
  const { currentEncounter } = useCurrentEncounter(patient);

  //----------------------------------------------------------------------------
  useEffect(() => {
    const initialize = async () => {
      if (!initialCondition?.code?.coding) {
        return;
      }

      const omimCoding = initialCondition.code.coding.find(
        (c) => c.system === SYSTEMS.OMIM
      );

      const orphaCoding = initialCondition.code.coding.find(
        (c) => c.system === SYSTEMS.ORPHA
      );

      const icdCoding = initialCondition.code.coding.find(
        (c) => c.system === ARKON.CS.ICD10.BASE
      );

      const mondoCoding = initialCondition.code.coding.find(
        (c) => c.system === SYSTEMS.MONDO
      );

      if (omimCoding) {
        const formatted: OmimSearchResult = {
          id: `${PREFIXES.OMIM}${omimCoding.code}`,
          name: omimCoding.display || initialCondition.code.text || '',
          description:
            initialCondition.note?.[0]?.text || 'No description available',
          mondoId: mondoCoding?.code,
        };

        setSelectedOmim(formatted);
        setSearchQuery(formatted.name);
        setOrphaCode(orphaCoding?.code || '');
        setIcdCode(icdCoding?.code || '');

        try {
          const hpo = await fetchDiseaseHPO(formatted.id);
          setHpoTerms(hpo);
        } catch (error) {
          console.error('Failed to fetch HPO terms:', error);
          setHpoTerms([]);
        }
      }
    };

    initialize();
  }, [initialCondition]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!patient?.id) {
      setOmimData([]);
      return;
    }

    const fetchOmimConditions = async () => {
      try {
        const response = await medplum.search('Condition', {
          patient: patient.id,
          code: `${SYSTEMS.OMIM}|`,
          _sort: '-_lastUpdated',
        });

        if (response.entry && response.entry.length > 0) {
          const omimItems = response.entry
            .map((entry) => {
              const condition = entry.resource as Condition;
              const coding = condition.code?.coding?.find(
                (c) => c.system === SYSTEMS.OMIM
              );

              if (coding && condition.id) {
                return {
                  id: coding.code || '',
                  name: coding.display || condition.code?.text || '',
                  description:
                    condition.note?.[0]?.text || 'No description available',
                  conditionId: condition.id,
                  verificationStatus:
                    condition.verificationStatus?.coding?.[0].display || '',
                };
              }
              return null;
            })
            .filter((item) => item !== null);

          setOmimData(omimItems);
        } else {
          setOmimData([]);
        }
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Error fetching OMIM conditions',
          message: error instanceof Error ? error.message : String(error),
        });

        setOmimData([]);
      }
    };

    fetchOmimConditions();
  }, [patient, medplum]);

  //----------------------------------------------------------------------------
  const searchOmim = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${SEARCH_OMIM_DISEASE}${encodeURIComponent(query)}`
      );
      const data = await response.json();

      // Filter only OMIM diseases
      const omimResults = data?.results?.filter((entry: any) =>
        entry.id?.startsWith(PREFIXES.OMIM)
      );

      if (omimResults.length === 0) {
        setSearchResults([]);
        return;
      }

      const formattedResults = omimResults.map((item: any) => ({
        id: item.id || item.curie,
        name: item.label || item.name,
        description: item.description,
        mondoId: item.mondoId,
      }));

      setSearchResults(formattedResults);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Error fetching OMIM diseases',
        message: error instanceof Error ? error.message : String(error),
      });

      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  //----------------------------------------------------------------------------
  const fetchOrphaCode = async (
    omimCode: string
  ): Promise<string | undefined> => {
    setIsFetchingOrpha(true);
    try {
      const numericCode = omimCode.replace(/^OMIM:/, '');
      const response = await fetch(
        `${FIND_ORPHA_FROM_OMIM}${encodeURIComponent(numericCode)}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            apiKey: 'whatever',
          },
        }
      );
      const data = await response.json();
      const firstRef = data?.References?.[0];
      return firstRef?.ORPHAcode?.toString();
    } catch (error) {
      console.error('Failed to fetch ORPHA code for', omimCode, error);
      return undefined;
    } finally {
      setIsFetchingOrpha(false);
    }
  };

  //----------------------------------------------------------------------------
  const fetchIcdCode = async (
    orphaCode: string
  ): Promise<string | undefined> => {
    setIsFetchingIcd(true);
    try {
      const numericCode = orphaCode.replace(/^ORPHA:/, '');
      const response = await fetch(
        `${FIND_ICD10_FROM_ORPHA}${encodeURIComponent(numericCode)}/ICD10`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            apiKey: 'whatever',
          },
        }
      );
      const data = await response.json();
      const firstRef = data?.References?.[0];
      return firstRef['Code ICD10'].toString();
    } catch (error) {
      console.error('Failed to fetch ICD-10 code for', orphaCode, error);
      return undefined;
    } finally {
      setIsFetchingIcd(false);
    }
  };

  //----------------------------------------------------------------------------
  const fetchDiseaseHPO = async (omimCode: string): Promise<HpoItem[]> => {
    interface RawHpoTerm {
      id: string;
      name: string;
      category: string;
      metadata: {
        sex: string;
        onset: string;
        frequency: string;
        sources: string[];
      };
    }

    interface RawHpoResponse {
      categories: Record<string, RawHpoTerm[]>;
    }

    try {
      const response = await fetch(
        `${HPO_ANNOTATION_API_URL}${encodeURIComponent(omimCode)}`
      );
      const data: RawHpoResponse = await response.json();

      if (!data.categories) {
        return [];
      }

      const terms: HpoItem[] = Object.entries(data.categories).flatMap(
        ([_category, categoryTerms]) =>
          categoryTerms.map((term) => ({
            id: term.id,
            name: term.name,
            value: term.category,
          }))
      );

      return terms;
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Error fetching HPO terms',
        message: error instanceof Error ? error.message : String(error),
      });
      return [];
    }
  };

  // ---------------------------------------------------------------------------
  const addSelectedSearchResult = async (
    newItem: OmimSearchResult,
    verificationStatus: string
  ) => {
    if (!patient || omimData.some((item) => item.id === newItem.id)) {
      return;
    }

    const codings = [
      {
        system: SYSTEMS.OMIM,
        code: newItem.id,
        display: newItem.name,
      },
      {
        system: SYSTEMS.MONDO,
        code: newItem.mondoId?.replace(PREFIXES.MONDO, ''),
      },
    ] as Coding[];

    const newCondition: Condition = {
      resourceType: 'Condition',
      subject: {
        reference: `Patient/${patient.id}`,
        display: patient ? formatHumanName(patient.name?.[0]) : '',
      },
      clinicalStatus: {
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE,
            code: 'active',
            display: 'Active',
          },
        ],
      },
      verificationStatus: {
        coding: [
          {
            code: verificationStatus,
            display: verificationStatus,
          },
        ],
      },
      code: {
        coding: codings,
        text: newItem.name,
      },
      note: [
        {
          text: newItem.description || 'No description available',
        },
      ],
      encounter: currentEncounter
        ? { reference: `Encounter/${currentEncounter.id}` }
        : undefined,
    };

    try {
      const savedCondition = await medplum.createResource(newCondition);

      const newOmimItem: OmimDisease = {
        id: newItem.id,
        name: newItem.name,
        description: newItem.description || 'No description available',
        conditionId: savedCondition.id,
        verificationStatus: verificationStatus,
      };

      setOmimData((prevData) => [newOmimItem, ...prevData]);
      setSelectedOmim(undefined);
      setTimeout(() => {
        clearSearch();
      }, 0);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Error adding OMIM data',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // ---------------------------------------------------------------------------
  const removeOmimData = async (id: string) => {
    const itemToRemove = omimData.find((item) => item.id === id);

    if (!itemToRemove?.conditionId) {
      return;
    }

    try {
      await medplum.deleteResource('Condition', itemToRemove.conditionId);

      setOmimData((prevData) => prevData.filter((item) => item.id !== id));
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Error removing OMIM data',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // ---------------------------------------------------------------------------
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchOmim(value);
  };

  // ---------------------------------------------------------------------------
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsLoading(false);
  };

  // ---------------------------------------------------------------------------
  const handleOmimSelection = async (value: string) => {
    if (!value) {
      setSelectedOmim(undefined);
      setOrphaCode(undefined);
      setHpoTerms([]);
      return;
    }

    const selectedOmim = searchResults.find((result) => result.name === value);
    if (!selectedOmim) {
      return;
    }
    setSelectedOmim(selectedOmim);

    const orpha = await fetchOrphaCode(selectedOmim.id);
    setOrphaCode(orpha);

    if (orpha) {
      const icd = await fetchIcdCode(orpha);
      setIcdCode(icd);
    }

    const hpo = await fetchDiseaseHPO(selectedOmim.id);
    setHpoTerms(hpo);
  };

  // ---------------------------------------------------------------------------
  return {
    omimData,
    searchQuery,
    setSearchQuery,
    searchResults,
    selectedOmim,
    setSelectedOmim,
    isLoading,
    handleSearchChange,
    handleOmimSelection,
    removeOmimData,
    addSelectedSearchResult,
    orphaCode,
    isFetchingOrpha,
    icdCode,
    isFetchingIcd,
    hpoTerms,
  };
};
